// LeadDashboardHelp.tsx
import React, { useMemo, useRef, useState } from "react";
import {
  Drawer,
  IconButton,
  TextField,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { DataTable } from "../../ui";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SettingsIcon from "@mui/icons-material/Settings";
import SecurityIcon from "@mui/icons-material/Security";
import FilterListIcon from "@mui/icons-material/FilterList";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { colors } from "../../../theme/tokens";


type PDFLink = { title: string; href: string };
type StepBlock = { title: string; bullets: string[] };

const DEFAULT_PDFS = [
  {
    title: "Lead Dashboard Help Manual",
    href: "https://mdimages-test.s3.amazonaws.com/ec026b13-379c-4120-b227-fda7adfd215b_Lead_Dashboard_Help_Manual.pdf",
  },
];

// ---- Static Content ----

const SETUP_STEPS: StepBlock[] = [
  { title: "Create the Business Service", bullets: ["Navigate to Organisation Dashboard → Click New Business Service → Fill in required fields."] },
  { title: "Create a Form", bullets: ["Define the form that will capture lead/record data for this service."] },
  { title: "Add Fields to the Form", bullets: ["Add all data fields needed — Field Name, Alias, Data Type, Required, Encrypted, Is Filter."] },
  { title: "Set Field-Level Permissions", bullets: ["Configure view/edit/none access per field per user profile."] },
  { title: "Configure Decision Board Entry", bullets: ["Set start and end dates and update the Business Service record."] },
  { title: "Accept and Assign Users", bullets: ["Accept the Business Service and assign platform users, designating an Owner."] },
  { title: "Verify in Records", bullets: ["Confirm the dashboard appears correctly under the Records section."] },
];

const FIELD_TYPES = [
  { type: "String", useFor: "Names, addresses, free text", notes: "Accepts any alphanumeric characters." },
  { type: "Int", useFor: "Numeric values (age, score, quantity)", notes: "Integer values only. No decimals." },
  { type: "Phone", useFor: "Mobile and telephone numbers", notes: "Validates phone number format." },
  { type: "Date", useFor: "Date of birth, follow-up dates", notes: "Date picker in the UI; stored as date." },
  { type: "Time", useFor: "Appointment or callback times", notes: "Time picker in the UI." },
  { type: "File", useFor: "Documents, images, PDFs, videos", notes: "Allows file upload. Specify accepted types." },
  { type: "Dropdown", useFor: "Status, category, grade, source", notes: "Predefined list of options. Supports status workflow logic." },
  { type: "Entity", useFor: "Relational references to other records", notes: "Complex linked-record type. Refer to Entity documentation." },
];

const DEFAULT_FIELDS = [
  { name: "lead_id", type: "STRING", desc: "Auto-generated unique identifier for every lead or record." },
  { name: "Contact Details", type: "PHONE", desc: "The lead's phone number. Auto-populated for WhatsApp leads." },
  { name: "Created", type: "STRING", desc: "Timestamp indicating when the lead record was created. System-generated." },
  { name: "Comment", type: "STRING", desc: "A free-text comment field for platform users to annotate a lead." },
  { name: "Audio Summary", type: "STRING", desc: "Reserved for future use." },
  { name: "AI Chat", type: "STRING", desc: "Reserved for future use." },
];

const PERMISSION_LEVELS = [
  { level: "Edit", desc: "The user can view the field and update its value." },
  { level: "View", desc: "The user can see the field's value but cannot make changes." },
  { level: "None", desc: "The field is hidden entirely from users in this profile." },
];

const FIELD_TYPE_COLUMNS = [
  { accessorKey: "type", header: "Type" },
  { accessorKey: "useFor", header: "Use For" },
  { accessorKey: "notes", header: "Notes" },
];

const DEFAULT_FIELD_COLUMNS = [
  { accessorKey: "name", header: "Field Name" },
  { accessorKey: "type", header: "Type" },
  { accessorKey: "desc", header: "Description" },
];

const PERMISSION_COLUMNS = [
  { accessorKey: "level", header: "Permission Level" },
  { accessorKey: "desc", header: "Behaviour" },
];

const SectionHeader: React.FC<{ title: string; subtitle?: string; id?: string }> = ({ title, subtitle, id }) => (
  <Box id={id} sx={{ mb: 1.25, mt: 3 }}>
    <Typography variant="subtitle1" fontWeight={700}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Box>
);

const StepAccordion: React.FC<{ steps: StepBlock[]; label: string; defaultExpanded?: boolean }> = ({
  steps,
  label,
  defaultExpanded,
}) => {
  const [active, setActive] = useState(0);
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ mb: 2, borderRadius: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>
          {label} ({steps.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stepper
          activeStep={active}
          alternativeLabel
          sx={{
            mb: 2,
            pb:{xs:0.5,sm:'auto'},
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            width: { xs: 353, sm: 'auto' },
            overflowX: { xs: 'scroll', sm: 'unset' },
            '& .MuiStepConnector-root': { zIndex: 0 },
            '& .MuiStepLabel-iconContainer': {
              position: 'relative',
              zIndex: 1,
              bgcolor: { xs: 'background.paper', sm: 'transparent' },
              px: { xs: 0.5, sm: 0 },
            },
          }}
          >
          {steps.map((s, i) => (
            <Step key={s.title} onClick={() => setActive(i)}>
              <StepLabel>{s.title}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {steps.map((s, idx) => {
          const isActive = idx === active;
          return (
            <Box
              key={s.title}
              sx={{
                mb: 1.25,
                p: 1.25,
                border: (t) => `1px dashed ${t.palette.divider}`,
                borderRadius: 2,
                bgcolor: (t) => (isActive ? t.palette.action.hover : "transparent"),
              }}
            >
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                {idx + 1}. {s.title}
              </Typography>
              <List dense sx={{ pt: 0 }}>
                {s.bullets.map((b, j) => (
                  <ListItem key={j} sx={{ py: 0.2 }}>
                    <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={`• ${b}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          );
        })}
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setActive((p) => Math.max(0, p - 1))}>
            Previous
          </Button>
          <Button variant="contained" onClick={() => setActive((p) => Math.min(steps.length - 1, p + 1))}>
            Next
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const LeadDashboardHelp: React.FC<{ pdfs?: PDFLink[] }> = ({ pdfs }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const jumpTo = (hash: string) => {
    const el = contentRef.current?.querySelector(hash);
    if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => setMenuEl(e.currentTarget);
  const handleCloseMenu = () => setMenuEl(null);
  const handlePrint = () => {
    window.open(DEFAULT_PDFS[0].href, "_blank");
  };

  const visiblePdfs = pdfs && pdfs.length > 0 ? pdfs : DEFAULT_PDFS;

  const sectionTexts = useMemo(() => {
    const flatten = (steps: StepBlock[]) => steps.map((s) => `${s.title} ${s.bullets.join(" ")}`).join(" ");

    return {
      overview: "business service lead dashboard record dashboard campaign configurable workspace manage leads",
      setup: flatten(SETUP_STEPS) + " business service form fields permissions decision board accept assign owner",
      fieldTypes: "string int phone date time file dropdown entity data types",
      defaultFields: "lead_id contact details created comment audio summary ai chat",
      permissions: "edit view none field-level permissions profile-based",
      decisionBoard: "business service planning static decision board start date end date accept assign",
    };
  }, []);

  const q = query.trim().toLowerCase();
  const matches = (sectionKey: keyof typeof sectionTexts) =>
    !q || sectionTexts[sectionKey].toLowerCase().includes(q);

  return (
    <div>
      <Tooltip title="Lead Dashboard Help">
        <IconButton onClick={() => setIsOpen(true)}>
          <HelpOutlineIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <Drawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)} sx={{ zIndex: 1400 }}>
        <Box
          sx={{
            width: {xs:420, sm:560},
            display: "flex",
            flexDirection: "column",
            height: "100%",
            bgcolor: (t) => (t.palette.mode === "light" ? "#fff" : "background.paper"),
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: colors.grey[50],
              backdropFilter: "saturate(1.2)",
            }}
           >
            <Stack direction="row" spacing={1} alignItems="center">
              <DashboardIcon />
              <Typography variant="h6" fontWeight={800}>
                Lead Dashboard • Help
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Menu
                anchorEl={menuEl}
                open={Boolean(menuEl)}
                onClose={handleCloseMenu}
              >
                {visiblePdfs.map((p) => (
                  <MenuItem
                    key={p.title}
                    component={p.href ? "a" : "li"}
                    href={p.href || undefined}
                    target={p.href ? "_blank" : undefined}
                    rel={p.href ? "noopener noreferrer" : undefined}
                    onClick={handleCloseMenu}
                  >
                    <PictureAsPdfIcon fontSize="small" style={{ marginRight: 8 }} />
                    {p.title}
                  </MenuItem>
                ))}
              </Menu>
              <Tooltip title="Print / Save as PDF">
                <IconButton onClick={handlePrint}>
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Search + Quick Jump */}
          <Box sx={{ px: 2, pt: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search setup, fields, permissions, decision board, owner…"
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" , rowGap:{xs:1 , sm:0}, columnGap:{xs:1 , sm:0} ,'& .MuiChip-root': { ml:{xs:0 , sm:'auto'}} }}>
              <Chip label="Overview" size="small" variant="outlined" onClick={() => jumpTo("#sec-overview")} sx={{ mb: 1 }} />
              <Chip label="Setup" size="small" variant="outlined" onClick={() => jumpTo("#sec-setup")} sx={{ mb: 1 }} />
              <Chip label="Field Types" size="small" variant="outlined" onClick={() => jumpTo("#sec-field-types")} sx={{ mb: 1 }} />
              <Chip label="Default Fields" size="small" variant="outlined" onClick={() => jumpTo("#sec-default-fields")} sx={{ mb: 1 }} />
              <Chip label="Permissions" size="small" variant="outlined" onClick={() => jumpTo("#sec-permissions")} sx={{ mb: 1 }} />
              <Chip label="Decision Board" size="small" variant="outlined" onClick={() => jumpTo("#sec-decision-board")} sx={{ mb: 1 }} />
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Scrollable Content */}
          <Box ref={contentRef} sx={{ px: 2, pb: 3, overflowY: "auto" }}>
            {/* Overview */}
            {matches("overview") && (
              <>
                <SectionHeader id="sec-overview" title="Overview" subtitle="Configurable workspace for managing leads and records." />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    A Business Service — also referred to as a Lead Dashboard, Record Dashboard, or Campaign — is a configurable workspace that lets your organisation manage incoming leads and records.
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={3}>
                      <Card variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: "action.hover", height: '100%' }}>
                        <Typography variant="body2" fontWeight={600}>Business Service</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={3}>
                      <Card variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: "action.hover", height: '100%' }}>
                        <Typography variant="body2" fontWeight={600}>Lead Dashboard</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={3}>
                      <Card variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: "action.hover", height: '100%' }}>
                        <Typography variant="body2" fontWeight={600}>Record Dashboard</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={3}>
                      <Card variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: "action.hover", height: '100%' }}>
                        <Typography variant="body2" fontWeight={600}>Campaign</Typography>
                      </Card>
                    </Grid>
                  </Grid>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    All four terms refer to the same entity in the platform.
                  </Alert>
                </Card>
              </>
            )}

            {/* Setup */}
            {matches("setup") && (
              <>
                <SectionHeader id="sec-setup" title="Setup Checklist" />
                <StepAccordion steps={SETUP_STEPS} label="Setup Steps" defaultExpanded />
              </>
            )}

            {/* Field Types */}
            {matches("fieldTypes") && (
              <>
                <SectionHeader id="sec-field-types" title="Supported Field Types" />
                <DataTable
                  columns={FIELD_TYPE_COLUMNS}
                  data={FIELD_TYPES}
                  aria-label="Supported field types"
                  muiTableContainerProps={{ sx: { borderRadius: 2, mb: 1 } }}
                />
              </>
            )}

            {/* Default Fields */}
            {matches("defaultFields") && (
              <>
                <SectionHeader id="sec-default-fields" title="Default Fields" />
                <DataTable
                  columns={DEFAULT_FIELD_COLUMNS}
                  data={DEFAULT_FIELDS}
                  aria-label="Default fields"
                  muiTableContainerProps={{ sx: { borderRadius: 2, mb: 1 } }}
                />
                <Alert severity="info">
                  These fields are system-generated and cannot be removed.
                </Alert>
              </>
            )}

            {/* Permissions */}
            {matches("permissions") && (
              <>
                <SectionHeader id="sec-permissions" title="Field-Level Permissions" />
                <DataTable
                  columns={PERMISSION_COLUMNS}
                  data={PERMISSION_LEVELS}
                  aria-label="Field-level permissions"
                  muiTableContainerProps={{ sx: { borderRadius: 2, mb: 1 } }}
                />
                <Alert severity="warning">
                  <strong>Profile-Based, Not User-Based:</strong> Permissions are applied at the profile level. Setting a permission for a profile changes access for every user who holds that profile.
                </Alert>
              </>
            )}

            {/* Decision Board */}
            {matches("decisionBoard") && (
              <>
                <SectionHeader id="sec-decision-board" title="Decision Board Entry" />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Before the Business Service can be assigned to users, it must be activated through the Business Service Decision Board.
                  </Typography>
                  <List dense sx={{ pt: 0 }}>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="• Navigate to: Business Service Planning → Static → Business Service Decision Board" />
                    </ListItem>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="• Locate your Business Service and click Edit" />
                    </ListItem>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="• Set Start Date (today) and End Date (one year from start)" />
                    </ListItem>
                  </List>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <strong>Owner vs. Standard User:</strong> Owners can view all leads. Standard users can only view leads assigned to them.
                  </Alert>
                </Card>
              </>
            )}

            {/* Footer */}
            <Box sx={{ mt: 2, px: 1.5, py: 1, borderTop: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Tip: Use the Download icon in the header to fetch the full manual PDF, or print this page to PDF.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </div>
  );
};

export default LeadDashboardHelp;
